import { useState } from 'react';
import {
  useCreatePath,
  useRecordContext,
  useNotify,
  useRefresh,
  RaRecord,
} from 'react-admin';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { VehiclePicker } from '../fleet/VehiclePicker';
import { SectionCard } from '../../shared/Panel';
import { planningStatusAppearance } from './planningStatus';
import { useStatuses } from './statusDictionary';
import { SOURCE_PRE_ORDER } from './recordSource';
import {
  changePlanningStatus,
  addCandidate,
  assignCar,
  setRisk,
  setValidUntil,
  toErrorMessage,
  requestUnassign,
  requestTradeTransfer,
  requestVehicleChange,
  MIN_UNASSIGN_REASON_LENGTH,
} from './orderActionsApi';
import { unassignRequestOf } from './UnassignRequestAlert';
import { recordScoped } from '../../shared/recordScoped';
import { useHasPermission } from '../PermissionGate';
import { useVariantScope, withVariantScopeNote } from './variantScope';
import { lateAssignmentWarningOf, windowWarningOf, withWindowWarning } from './variantWindow';
import { LateAssignmentWarning } from './LateAssignmentWarning';
import { useAssignOverride } from './AssignOverride';
import { useFeature } from '../featureFlags/FeatureGate';
import { TeamLeaderAssignsButton, usePlannerSeesDisabledAssign } from './AssignByTeamLeader';
import {
  FEATURE_UNASSIGN_BEFORE_DEPARTURE,
  beforeDepartureText,
  departedText,
  departureEstimateOf,
  selfUnassignAllowed,
} from './unassignDeparture';
import {
  rankFor,
  rankText,
  shortListKey,
  useCandidateRanks,
  type CandidateRanks,
} from './candidateRanks';
import {
  FEATURE_TRADE_TRANSFER_REASONS,
  transferKindOf,
  useTradeReasons,
} from '../tradeReasons/tradeReasonsApi';
import { NO_REASON, TransferReasonFields, transferReasonReady } from './TransferReasonFields';
import {
  FEATURE_TRADE_TRANSFER_BEFORE_DEPARTURE,
  FEATURE_VEHICLE_CHANGE_REQUEST,
  departedTradeTransferText,
  departedVehicleChangeText,
  selfTradeTransferText,
  tradeTransferAccessOf,
  vehicleChangeRequestableOf,
} from './changeRequests';

/** Stan „Przekazane do Trade” — kod ze słownika, jak w API. */
const HANDED_TO_TRADE = 'handed_to_trade';

/**
 * Akcje na zleceniu.
 *
 * ## Skąd wiadomo, co wolno
 *
 * Z API. Szczegóły zlecenia niosą `mozliweOperacje` (patrz
 * `apps/api/src/user-offers/mozliwe-operacje.ts`) — listę dozwolonych stanów,
 * informację czy da się obsadzić i **powód, gdy się nie da**. Front nie ma
 * własnej kopii grafu przejść i nie zgaduje; pokazuje to, co przyszło.
 *
 * Dzięki temu przycisk zablokowany zawsze ma przy sobie wyjaśnienie. Wyszarzona
 * akcja bez powodu jest gorsza niż jej brak — planista widzi, że czegoś nie
 * może, i nie wie dlaczego.
 *
 * ## Wybór pojazdu
 *
 * Wariant dodaje się przez wyszukiwarkę po kartotece floty (`VehiclePicker`).
 * Wcześniej było tu pole na identyfikator wpisywany z ręki — przy 1210
 * pojazdach planista nie ma skąd znać numeru wewnętrznego, a pomyłka w cyfrze
 * podstawiała inne auto bez żadnego sygnału.
 *
 * Obsadzenie wybiera się już tylko spośród **dodanych wariantów**, nie z całej
 * kartoteki: §12 rozdziału FTL każe zwolnić pozostałe warianty przy obsadzeniu,
 * więc auto musi najpierw być wariantem.
 *
 * ## Wniosek o odpięcie
 *
 * Planista nie odpina sam auta od obsadzonego zlecenia — o oddaniu zlecenia
 * do puli decyduje przełożony (omówienie 01.10.2026). Zamiast ukrytej opcji
 * „Zmień stan” dostaje „Wnioskuj o odpięcie” z powodem; do których stanów,
 * mówi API (`unassignRequestTargets`). Gdy wniosek już czeka, wiersza nie
 * ma — wniosek i jego los pokazuje baner na górze karty.
 *
 * Od 20.10.2026 (przełącznik `unassignBeforeDeparture`, odpowiedź
 * Marathonu/Euvic 16.10.2026, pyt. 6b): dopóki auto nie wyruszyło na
 * załadunek, planista odpina sam — „Odepnij” z powodem zamiast wniosku;
 * po wyjeździe wniosek z informacją „auto wyruszyło na załadunek (szac.
 * …)”. Czy auto wyruszyło, mówi API (`unassignNeedsRequest`,
 * `departureEstimate`).
 *
 * ## Przekazanie do Trade i zmiana pojazdu po wyjeździe (21.10.2026)
 *
 * Makieta back office v2, B6 (BR-FTL-05, ścieżki MIX-04 i FTL-01; decyzja
 * użytkownika 20.10.2026, D5 — ta sama granica co odpięcie). Za
 * przełącznikiem `tradeTransferBeforeDeparture` planista przekazuje
 * obsadzone zlecenie swojego auta do Trade sam („Przekaż do Trade”, powód
 * z listy), dopóki auto nie wyruszyło; potem — „Wnioskuj o przekazanie do
 * Trade”. Za `vehicleChangeRequest` po wyjeździe — „Wnioskuj o zmianę
 * pojazdu” z proponowanym autem; przed wyjazdem zmienia się pojazd bez
 * wniosku: „Odepnij” (cofnięcie do „Wstępnie planowane” zostawia auto
 * wariantem) i nowy wariant, a obsadza Team Leader. Co wolno, mówi API
 * (`tradeTransferBeforeDeparture`, `vehicleChangeRequestable`).
 */

/**
 * Układ wiersza akcji: pole rośnie, ale nie ponad 480 px, a przycisk nigdy
 * się nie ściska ani nie łamie. Przy szerokich ekranach pole na całą
 * szerokość karty (1500 px na jedno słowo) spychało przycisk w „ZGŁOŚ /
 * RYZYKO”; przy wąskich wiersz zawija się pod polem zamiast łamać napis.
 */
const ROW_SX = { alignItems: 'flex-start', flexWrap: 'wrap' } as const;
const FIELD_SX = { flex: '1 1 160px', maxWidth: 480, minWidth: 0 } as const;
const BUTTON_SX = { whiteSpace: 'nowrap', flexShrink: 0 } as const;

type OrderRecord = RaRecord & {
  availableActions?: {
    allowedStates?: string[];
    /** Stan → opis wymogu roli; przejścia, których ten użytkownik nie wykona (Q-08). */
    blockedByRole?: Record<string, string>;
    /** Stan → powód; spółka zlecenia nie przekazuje do Trade (29.09.2026). */
    blockedByCompany?: Record<string, string>;
    canAssign?: boolean;
    assignBlockedReason?: string | null;
    /** „Obsadź” to tu zmiana obsadzenia — zlecenie już ma auto floty. */
    changesAssignment?: boolean;
    /**
     * Stany docelowe, do których przejście wymaga powodu
     * (`OrderTransition.requiresReason`). Dziś API tego nie przysyła —
     * wtedy nie udajemy, że powód jest opcjonalny (patrz `reasonField`).
     */
    requiresReason?: string[];
  };
};

/** Auto obsadzające zlecenie — numer rejestracyjny, a bez niego identyfikator. */
const assignedCarLabel = (record: RaRecord): string | null => {
  const car = record.assignedCar as
    | { registrationNumber?: string | null; name?: string | null }
    | null
    | undefined;
  if (car?.registrationNumber) return car.registrationNumber;
  if (car?.name) return car.name;
  return record.assignedCarId != null ? `#${String(record.assignedCarId)}` : null;
};

/** Id auta obsadzającego — do odsyłacza do karty pojazdu; bez id odsyłacza nie ma. */
const assignedCarIdOf = (record: RaRecord): number | null => {
  const car = record.assignedCar as { id?: unknown } | null | undefined;
  const id = car?.id ?? record.assignedCarId;
  if (id === null || id === undefined || id === '') return null;
  const n = Number(id);
  return Number.isInteger(n) ? n : null;
};

/**
 * Numer obsadzonego auta jako odsyłacz do jego karty (omówienie 01.10.2026)
 * — zwykły link w tej samej karcie przeglądarki. Ścieżka z `useCreatePath`,
 * jak przy kopercie zapisu do VIA, nie sklejana ręcznie.
 */
const AssignedCarLink = ({ carId, label }: { carId: number; label: string }) => {
  const createPath = useCreatePath();
  return (
    <Link to={createPath({ resource: 'cars', type: 'show', id: carId })}>
      <strong>{label}</strong>
    </Link>
  );
};

/**
 * Czy pokazać „Termin ważności” (omówienie 01.10.2026). Termin ważności to
 * działanie na prezleceniu (§15) — przy zleceniu z TMS klienta tylko
 * zajmował miejsce. Termin już ustawiony pokazujemy przy każdym rekordzie,
 * żeby dało się go zdjąć. Prezlecenie rozpoznajemy po źródle
 * (`sourceSystem`), nie po stanie: wiersz z prezlecenia ma to źródło także
 * po akceptacji, gdy jest już „gotowe do planowania”.
 */
export function showsValidUntil(record: {
  sourceSystem?: unknown;
  validUntil?: unknown;
  // Rekord react-admina niesie dowolne pola; bez sygnatury indeksu typ
  // z samymi polami opcjonalnymi jest dla TS „słaby” i rekordu nie przyjmie.
  [field: string]: unknown;
}): boolean {
  if (record.sourceSystem === SOURCE_PRE_ORDER) return true;
  const v = record.validUntil;
  return v !== null && v !== undefined && v !== '';
}

/**
 * Etykieta i wymagalność pola powodu dla wybranej krawędzi.
 *
 * E2e na dev (28.09.2026): pole mówiło „Powód (opcjonalny)”, a przy
 * krawędziach z `requiresReason` API odmawiało 400. Gdy API mówi, które
 * krawędzie wymagają powodu — pole jest wymagane albo opcjonalne według
 * wybranego stanu. Gdy nie mówi — nie zgadujemy w żadną stronę.
 */
export function reasonField(
  selectedState: string,
  requiresReason: string[] | undefined,
): { label: string; required: boolean; helperText: string } {
  if (!Array.isArray(requiresReason)) {
    return {
      label: 'Powód',
      required: false,
      helperText: selectedState ? 'Wymagany przy niektórych zmianach stanu' : ' ',
    };
  }
  if (!selectedState) return { label: 'Powód', required: false, helperText: ' ' };
  return requiresReason.includes(selectedState)
    ? { label: 'Powód (wymagany)', required: true, helperText: 'Wymagany przy tej zmianie stanu' }
    : { label: 'Powód (opcjonalny)', required: false, helperText: ' ' };
}

/**
 * Komunikat po zapisie terminu ważności. Rozstrzyga odpowiedź serwera
 * (`validUntil` zapisanego rekordu); gdy jej nie ma — to, o co prosiliśmy.
 */
export function validUntilMessage(
  requested: string | null,
  saved: { validUntil?: unknown } | undefined,
): string {
  const result =
    saved && typeof saved === 'object' && 'validUntil' in saved
      ? saved.validUntil
      : requested;
  return result == null || result === '' ? 'Termin ważności usunięty' : 'Termin ważności ustawiony';
}

/**
 * Pozycja wariantu na liście wyboru obsadzenia: „WGM123 (3. miejsce na 41,
 * dodany ręcznie)”. Bez policzonej pozycji zostaje sam numer — lista działa dalej.
 */
export function assignOptionLabel(
  candidacy: RaRecord,
  ranks: CandidateRanks | null,
): string {
  const plate =
    ((candidacy.car as RaRecord | undefined)?.registrationNumber as string | undefined) ??
    `#${String(candidacy.carId)}`;
  const rank = rankFor(ranks, candidacy.carId);
  const parts = [rankText(rank, ranks?.total ?? null)];
  if (rank?.addedManually) parts.push('dodany ręcznie');
  const extra = parts.filter((p): p is string => p !== null);
  return extra.length > 0 ? `${plate} (${extra.join(', ')})` : plate;
}

const OrderActionsPanel = () => {
  const record = useRecordContext<OrderRecord>();
  const notify = useNotify();
  const refresh = useRefresh();
  // Dozwolone stany przychodzą z API jako KODY — mogą być spoza enuma frontu
  // (słownik jest konfiguracją). Etykiety bierzemy ze słownika.
  const states = useStatuses();
  const [busy, setBusy] = useState(false);

  const [newState, setNewState] = useState('');
  const [stateReason, setStateReason] = useState('');
  // Powód z listy przy przekazaniu do Trade i zwrocie (A6, 20.10.2026).
  const [stateReasonCode, setStateReasonCode] = useState(NO_REASON);
  const [riskReason, setRiskReason] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [assignCarId, setAssignCarId] = useState('');
  // Powód zmiany pojazdu — opcjonalny (porównanie z makietą FTL, 13.10.2026).
  const [assignReason, setAssignReason] = useState('');
  const [pickedCarId, setPickedCarId] = useState<number | null>(null);
  // Okno „Obsadzić mimo braku czasu?” (22.10.2026, decyzja użytkownika pkt 3).
  const [lateConfirmOpen, setLateConfirmOpen] = useState(false);
  const [unassignTarget, setUnassignTarget] = useState('');
  const [unassignReason, setUnassignReason] = useState('');
  // Przekazanie do Trade przez planistę i wnioski po wyjeździe (21.10.2026).
  const [tradeReasonCode, setTradeReasonCode] = useState(NO_REASON);
  const [tradeComment, setTradeComment] = useState('');
  const [proposedCarId, setProposedCarId] = useState<number | null>(null);
  const [vehicleChangeReason, setVehicleChangeReason] = useState('');
  // Warianty to planowanie FTL, obsadzenie ma własne uprawnienie — bez
  // uprawnienia kontrolki są ukryte, nie wyszarzone (01.10.2026).
  const canPlan = useHasPermission('FTL_PLANNING');
  const canAssignVehicle = useHasPermission('FTL_ASSIGN');
  const plannerSeesDisabledAssign = usePlannerSeesDisabledAssign();
  // „Obsadź mimo blokady” — Team Leader (16.10.2026, pyt. 54e).
  const override = useAssignOverride();
  // Gość tylko ogląda (01.10.2026): bez prawa do zmian panelu akcji nie ma
  // wcale; obsadzone auto widać w sekcji wariantów.
  const canChange = useHasPermission('CHANGE_DATA');
  // Planista wnioskuje o odpięcie tylko swoich aut — ta sama reguła co przy
  // wariantach (`variantScope.ts`, decyzja użytkownika 13.10.2026).
  const inPlannerScope = useVariantScope();
  // Odpięcie do wyjazdu na załadunek (decyzja użytkownika 20.10.2026).
  const beforeDepartureOn = useFeature(FEATURE_UNASSIGN_BEFORE_DEPARTURE);
  // Pozycja wariantów w podpowiedziach przy wyborze obsadzenia (status
  // 29.09.2026). Hak przed `return` — kolejność haków nie może zależeć od rekordu.
  const liveCandidacies = ((record?.candidates as RaRecord[] | undefined) ?? []).filter(
    (k) => k.status === 'active',
  );
  const ranks = useCandidateRanks(
    record?.id,
    canPlan && liveCandidacies.length > 0 ? shortListKey(liveCandidacies, record?.assignedCarId) : '',
  );
  // Powód przekazania do Trade i zwrotu z listy (makieta back office v2, A6;
  // przełącznik `tradeTransferReasons`, 20.10.2026). Hak przed `return`.
  const transferReasonsOn = useFeature(FEATURE_TRADE_TRANSFER_REASONS);
  const transferReasons = useTradeReasons(transferReasonsOn && canChange);
  // Wnioski po wyjeździe auta (makieta back office v2, B6, 21.10.2026).
  const tradeTransferOn = useFeature(FEATURE_TRADE_TRANSFER_BEFORE_DEPARTURE);
  const vehicleChangeOn = useFeature(FEATURE_VEHICLE_CHANGE_REQUEST);
  const dualTrackOn = useFeature('tradeDualTrack');

  if (!record || !canChange) return null;

  const actions = record.availableActions ?? {};
  const allowedStates = actions.allowedStates ?? [];
  // Przejścia zablokowane rolą znikają z listy (omówienie 01.10.2026: funkcje
  // bez uprawnień są ukryte, nie wyszarzone). Wyszarzone z powodem zostają
  // przejścia zamknięte przez spółkę zlecenia (bez kont giełdowych nie ma
  // przekazania do Trade, 29.09.2026) — żadna rola ich nie otworzy, a powód
  // mówi, czego brakuje. Serwer i tak odmówi (bramka jest na backendzie).
  const blockedByRole = actions.blockedByRole ?? {};
  const offeredStates = allowedStates.filter((s) => !(s in blockedByRole));
  const unavailable = actions.blockedByCompany ?? {};
  const selectableStates = offeredStates.filter((s) => !(s in unavailable));
  // Gdy każde przejście zamyka rola, kontrolki zmiany stanu nie ma wcale.
  // Stan bez żadnego przejścia zostaje z wyjaśnieniem — to blokada stanu.
  const showStateChange = allowedStates.length === 0 || offeredStates.length > 0;
  const reason = reasonField(newState, actions.requiresReason);
  // Przekazanie do Trade albo zwrot do FTL — dział stanu ze słownika (A6).
  const departmentOf = (code: unknown) => states.find((s) => s.code === code)?.department ?? null;
  const transferKind =
    transferReasonsOn && newState
      ? transferKindOf(departmentOf(record.planningStatus), departmentOf(newState))
      : null;
  // Kontrakt `data-testid` paczki (S-22.10-02): „Zmień” przy wybranym zwrocie z Trade do FTL to
  // „Zwróć do FTL” (`trd-zlecenie-zwrot-ftl`) — niezależnie od powodów z listy.
  const returnsToFtl =
    !!newState && transferKindOf(departmentOf(record.planningStatus), departmentOf(newState)) === 'return';
  const stateChangeReady =
    transferKind === null
      ? !(reason.required && !stateReason.trim())
      : transferReasonReady({
          required: reason.required,
          code: stateReasonCode,
          comment: stateReason,
          reasons: transferReasons ?? [],
        });
  const candidacies = ((record.candidates as RaRecord[] | undefined) ?? []).filter(
    (k) => k.status === 'active',
  );
  // Zlecenie obsadzone (także importem z TMS, bez wariantu) pokazuje, jakie
  // auto je obsadza, a „obsadź” staje się zmianą auta — spośród wariantów
  // innych niż obsadzony (e2e 28.09.2026: namawiało do obsadzenia).
  const changesAssignment = actions.changesAssignment === true;
  const assignedLabel = assignedCarLabel(record);
  const assignedId = assignedCarIdOf(record);
  const assignOptions = changesAssignment
    ? candidacies.filter((k) => k.carId !== record.assignedCarId)
    : candidacies;
  // Wybrane auto pewnie nie zmieści się w oknach (22.10.2026, pkt 3) — wiersz
  // kandydata z tego samego przeliczenia co lista rekomendacji. Ostrzeżenie,
  // nie blokada; brak wyliczeń = bez ostrzeżenia (API i tak zapisze wpis).
  const lateWarning = assignCarId
    ? lateAssignmentWarningOf(rankFor(ranks, assignCarId)?.candidate ?? null)
    : null;
  // Wniosek o odpięcie (01.10.2026): stany, do których pytający może prosić
  // o odpięcie zamiast odpiąć sam — z API. Gdy wniosek już czeka, nie
  // składamy drugiego (serwer i tak odmówi).
  const unassignTargets = Array.isArray(record.unassignRequestTargets)
    ? (record.unassignRequestTargets as unknown[]).filter(
        (s): s is string => typeof s === 'string',
      )
    : [];
  // Przy cudzym aucie planisty (opiekun FTL inny niż wskazany) wiersza nie
  // ma — API i tak odmawia 403 (13.10.2026).
  const assignedCaretaker = (record.assignedCar as RaRecord | null | undefined)?.ftlCaretaker;
  const ownCar = inPlannerScope(typeof assignedCaretaker === 'string' ? assignedCaretaker : null);
  // Przed wyjazdem na załadunek planista odpina sam (20.10.2026) — wtedy
  // zamiast wniosku wiersz „Odepnij”; po wyjeździe wniosek jak dotąd.
  const departure = departureEstimateOf(record);
  const selfUnassign = selfUnassignAllowed(record, beforeDepartureOn);
  const showSelfUnassign = canPlan && unassignTargets.length > 0 && selfUnassign && ownCar;
  const showUnassignRequest =
    canPlan &&
    unassignTargets.length > 0 &&
    !selfUnassign &&
    unassignRequestOf(record) === null &&
    ownCar;
  // Informacja o wyjeździe przy wniosku — tylko gdy API ją podało.
  const departedInfo =
    beforeDepartureOn && departure?.departed === true ? departure : null;
  const chosenUnassignTarget = unassignTargets.includes(unassignTarget)
    ? unassignTarget
    : (unassignTargets[0] ?? '');
  // Przekazanie obsadzonego zlecenia do Trade i zmiana pojazdu (21.10.2026):
  // co wolno, mówi API; front pokazuje tylko przy własnym aucie planisty
  // i gdy żaden wniosek nie czeka (jeden na zlecenie — serwer i tak odmówi).
  const noPendingRequest = unassignRequestOf(record) === null;
  const tradeAccess = tradeTransferAccessOf(record, tradeTransferOn);
  const showSelfTradeTransfer = canPlan && ownCar && tradeAccess?.self === true;
  const showTradeTransferRequest =
    canPlan && ownCar && tradeAccess?.request === true && noPendingRequest;
  const showVehicleChangeRequest =
    canPlan && ownCar && noPendingRequest && vehicleChangeRequestableOf(record, vehicleChangeOn);
  // Powód przekazania obsadzonego zlecenia — wymagany wg słownika przejść.
  const tradeReasonRequired = Array.isArray(actions.requiresReason)
    ? actions.requiresReason.includes(HANDED_TO_TRADE)
    : true;
  const tradeReasonFromList =
    transferReasonsOn &&
    transferReasonReady({
      required: tradeReasonRequired,
      code: tradeReasonCode,
      comment: tradeComment,
      reasons: transferReasons ?? [],
    });
  const selfTradeTransferReady = transferReasonsOn
    ? tradeReasonFromList
    : !tradeReasonRequired || tradeComment.trim() !== '';
  // Wniosek czyta przełożony — bez listy co najmniej 10 znaków, jak wniosek o odpięcie.
  const tradeTransferRequestReady = transferReasonsOn
    ? tradeReasonFromList && (tradeReasonCode !== NO_REASON || tradeComment.trim() !== '')
    : tradeComment.trim().length >= MIN_UNASSIGN_REASON_LENGTH;
  const tradeVariantsNote = dualTrackOn
    ? 'Pojazd zejdzie ze zlecenia i zostanie wariantem — flota może dalej szukać auta.'
    : 'Pojazd zejdzie ze zlecenia, a warianty floty zostaną zwolnione.';
  // Wniosek o przekazanie czyta przełożony — powód wymagany zawsze, także gdy
  // słownik zdejmuje go z samego przekazania (S-21.10-20, 21.10.2026).
  const tradeReasonInputs = (required: boolean) =>
    transferReasonsOn ? (
      <TransferReasonFields
        kind="handover"
        required={required}
        reasons={transferReasons}
        code={tradeReasonCode}
        comment={tradeComment}
        onCode={setTradeReasonCode}
        onComment={setTradeComment}
        disabled={busy}
      />
    ) : (
      <TextField
        size="small"
        label={required ? 'Powód przekazania (wymagany)' : 'Powód przekazania (opcjonalnie)'}
        required={required}
        value={tradeComment}
        onChange={(e) => setTradeComment(e.target.value)}
        disabled={busy}
        sx={FIELD_SX}
        helperText="Trafi do historii zmian"
      />
    );
  const resetTradeReason = () => {
    setTradeReasonCode(NO_REASON);
    setTradeComment('');
  };
  const tradeReasonPayload = () => ({
    reason: tradeComment,
    ...(transferReasonsOn && tradeReasonCode !== NO_REASON ? { reasonCode: tradeReasonCode } : {}),
  });

  /** Każda akcja kończy się odświeżeniem — stan mógł się zmienić kaskadowo. */
  const run = async <T,>(
    action: () => Promise<T>,
    successMessage: string | ((result: T) => string),
  ) => {
    setBusy(true);
    try {
      const result = await action();
      notify(
        typeof successMessage === 'function' ? successMessage(result) : successMessage,
        { type: 'success' },
      );
      refresh();
    } catch (e) {
      // Treść odmowy z backendu jest pisana dla operatora — pokazujemy ją
      // w całości, zamiast „wystąpił błąd".
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  /**
   * „Obsadź” / „Zmień pojazd” wybranym wariantem. Odmowa z samymi blokadami
   * z uprawnień przy koncie Team Leadera otwiera okno „Obsadź mimo blokady”
   * (16.10.2026, pyt. 54e) — potwierdzenie ponawia obsadzenie z powodem.
   */
  const assignSelected = async () => {
    const carId = Number(assignCarId);
    const success = changesAssignment ? 'Obsadzenie zmienione' : 'Zlecenie obsadzone';
    // „Obsadzono mimo braku czasu” z odpowiedzi API (22.10.2026) — komunikat
    // z ostrzeżeniem zamiast samego sukcesu.
    const done = (response?: unknown) => {
      setAssignReason('');
      notify(withWindowWarning(success, response), {
        type: windowWarningOf(response) ? 'warning' : 'success',
      });
      refresh();
    };
    setBusy(true);
    try {
      done(await assignCar(record.id, carId, changesAssignment ? { reason: assignReason } : undefined));
    } catch (e) {
      const offered = override.offer(e, async (reason) => {
        done(await assignCar(record.id, carId, { reason, overrideBlocking: true }));
      });
      if (!offered) notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  /**
   * Termin ważności — komunikat z tego, co zapisał serwer, nie z przycisku.
   * Weryfikacja 28.09: po „Zdejmij termin” widać było „Termin ustawiony”.
   * Kod przycisków miał właściwe teksty; najbardziej prawdopodobne — pole
   * daty trzymało dalej wybraną datę, a „Termin ustawiony” z poprzedniego
   * kliknięcia stał jeszcze w kolejce powiadomień react-admina (pokazuje je
   * po jednym, 4 s). Komunikat rozstrzyga teraz odpowiedź serwera, a pole
   * czyścimy po udanym zapisie.
   */
  const runValidUntil = (value: string | null) =>
    run(async () => {
      const saved = (await setValidUntil(record.id, value)) as
        | { validUntil?: unknown }
        | undefined;
      setExpiryDate('');
      return saved;
    }, (saved) => validUntilMessage(value, saved));

  // „Działania”, nie „Akcje” — spójnie z „Dziennikiem działań” (01.10.2026).
  return (
    <SectionCard title="Akcje">
      <Stack spacing={2}>
        {/* ── stan ─────────────────────────────────────────────────────── */}
        {showStateChange && (
        <Stack direction="row" spacing={1} useFlexGap sx={ROW_SX}>
          <TextField
            select
            size="small"
            label="Zmień stan"
            value={newState}
            onChange={(e) => setNewState(e.target.value)}
            disabled={busy || selectableStates.length === 0}
            sx={FIELD_SX}
            helperText={
              allowedStates.length === 0
                ? 'Tego stanu nie można zmienić'
                : selectableStates.length === 0
                  ? 'Zmiana stanu jest niedostępna — powód przy każdej opcji'
                  : ' '
            }
          >
            {offeredStates.map((s) => (
              <MenuItem key={s} value={s} disabled={s in unavailable}>
                {planningStatusAppearance(s, states).label}
                {s in unavailable ? ` — ${unavailable[s]}` : ''}
              </MenuItem>
            ))}
          </TextField>
          {transferKind ? (
            <TransferReasonFields
              kind={transferKind}
              required={reason.required}
              reasons={transferReasons}
              code={stateReasonCode}
              comment={stateReason}
              onCode={setStateReasonCode}
              onComment={setStateReason}
              disabled={busy}
            />
          ) : (
            <TextField
              size="small"
              label={reason.label}
              required={reason.required}
              value={stateReason}
              onChange={(e) => setStateReason(e.target.value)}
              disabled={busy}
              sx={FIELD_SX}
              helperText={reason.helperText}
            />
          )}
          <Button
            sx={BUTTON_SX}
            variant="contained"
            disabled={busy || !newState || !stateChangeReady}
            data-testid={returnsToFtl ? 'trd-zlecenie-zwrot-ftl' : undefined}
            onClick={() =>
              run(async () => {
                await changePlanningStatus(record.id, newState, {
                  reason: stateReason,
                  ...(transferKind && stateReasonCode !== NO_REASON
                    ? { reasonCode: stateReasonCode }
                    : {}),
                });
                // Po udanej zmianie czyścimy wybór i powód — inaczej ten sam
                // powód dało się jednym kliknięciem użyć przy kolejnej zmianie.
                // Przy odmowie zostają, żeby operator mógł poprawić i ponowić.
                setNewState('');
                setStateReason('');
                setStateReasonCode(NO_REASON);
              }, 'Stan zmieniony')
            }
          >
            Zmień
          </Button>
        </Stack>
        )}

        {/* ── odpięcie przez planistę przed wyjazdem na załadunek ─────── */}
        {showSelfUnassign && (
          <Stack spacing={0.5} data-testid="unassign-self">
            <Stack direction="row" spacing={1} useFlexGap sx={ROW_SX}>
              <TextField
                select
                size="small"
                label="Po odpięciu zlecenie wróci do"
                value={chosenUnassignTarget}
                onChange={(e) => setUnassignTarget(e.target.value)}
                disabled={busy}
                sx={FIELD_SX}
                helperText={beforeDepartureText(departure)}
              >
                {unassignTargets.map((s) => (
                  <MenuItem key={s} value={s}>
                    {planningStatusAppearance(s, states).label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label="Powód odpięcia (wymagany)"
                required
                value={unassignReason}
                onChange={(e) => setUnassignReason(e.target.value)}
                disabled={busy}
                sx={FIELD_SX}
                helperText={`Co najmniej ${MIN_UNASSIGN_REASON_LENGTH} znaków — trafi do historii zmian`}
              />
              <Button
                sx={BUTTON_SX}
                variant="outlined"
                disabled={
                  busy ||
                  !chosenUnassignTarget ||
                  unassignReason.trim().length < MIN_UNASSIGN_REASON_LENGTH
                }
                onClick={() =>
                  run(async () => {
                    await changePlanningStatus(record.id, chosenUnassignTarget, {
                      reason: unassignReason.trim(),
                    });
                    setUnassignReason('');
                  }, 'Pojazd odpięty od zlecenia')
                }
              >
                Odepnij
              </Button>
            </Stack>
            {departure?.reason ? (
              <Typography variant="caption" color="text.secondary">
                {departure.reason}
              </Typography>
            ) : null}
          </Stack>
        )}

        {/* ── wniosek o odpięcie — dla planisty bez prawa do odpięcia ──── */}
        {showUnassignRequest && departedInfo && (
          <Stack spacing={0.25} data-testid="unassign-departed">
            <Typography variant="body2">{departedText(departedInfo)}</Typography>
            {departedInfo.reason ? (
              <Typography variant="caption" color="text.secondary">
                {departedInfo.reason}
              </Typography>
            ) : null}
          </Stack>
        )}
        {showUnassignRequest && (
          <Stack direction="row" spacing={1} useFlexGap sx={ROW_SX} data-testid="unassign-request">
            <TextField
              select
              size="small"
              label="Po odpięciu zlecenie wróci do"
              value={chosenUnassignTarget}
              onChange={(e) => setUnassignTarget(e.target.value)}
              disabled={busy}
              sx={FIELD_SX}
              helperText="Odpięcie zatwierdza przełożony"
            >
              {unassignTargets.map((s) => (
                <MenuItem key={s} value={s}>
                  {planningStatusAppearance(s, states).label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Powód wniosku (wymagany)"
              required
              value={unassignReason}
              onChange={(e) => setUnassignReason(e.target.value)}
              disabled={busy}
              sx={FIELD_SX}
              helperText={`Co najmniej ${MIN_UNASSIGN_REASON_LENGTH} znaków — przeczyta go przełożony`}
            />
            <Button
              sx={BUTTON_SX}
              variant="outlined"
              disabled={
                busy ||
                !chosenUnassignTarget ||
                unassignReason.trim().length < MIN_UNASSIGN_REASON_LENGTH
              }
              onClick={() =>
                run(async () => {
                  await requestUnassign(record.id, chosenUnassignTarget, unassignReason);
                  setUnassignReason('');
                }, 'Wniosek o odpięcie wysłany do przełożonego')
              }
            >
              Wnioskuj o odpięcie
            </Button>
          </Stack>
        )}

        {/* ── przekazanie obsadzonego zlecenia do Trade (21.10.2026) ──── */}
        {showSelfTradeTransfer && (
          <Stack spacing={0.5} data-testid="trade-transfer-self">
            <Typography variant="body2">{selfTradeTransferText(departure)}</Typography>
            <Stack direction="row" spacing={1} useFlexGap sx={ROW_SX}>
              {tradeReasonInputs(tradeReasonRequired)}
              <Button
                sx={BUTTON_SX}
                variant="outlined"
                disabled={busy || !selfTradeTransferReady}
                onClick={() =>
                  run(async () => {
                    await changePlanningStatus(record.id, HANDED_TO_TRADE, tradeReasonPayload());
                    resetTradeReason();
                  }, 'Zlecenie przekazane do Trade')
                }
              >
                Przekaż do Trade
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {tradeVariantsNote}
              {departure?.reason ? ` ${departure.reason}` : ''}
            </Typography>
          </Stack>
        )}
        {showTradeTransferRequest && (
          <Stack spacing={0.5} data-testid="trade-transfer-request">
            <Typography variant="body2">{departedTradeTransferText(departure)}</Typography>
            {departure?.reason ? (
              <Typography variant="caption" color="text.secondary">
                {departure.reason}
              </Typography>
            ) : null}
            <Stack direction="row" spacing={1} useFlexGap sx={ROW_SX}>
              {tradeReasonInputs(true)}
              <Button
                sx={BUTTON_SX}
                variant="outlined"
                disabled={busy || !tradeTransferRequestReady}
                onClick={() =>
                  run(async () => {
                    await requestTradeTransfer(record.id, tradeReasonPayload());
                    resetTradeReason();
                  }, 'Wniosek o przekazanie do Trade wysłany do przełożonego')
                }
              >
                Wnioskuj o przekazanie do Trade
              </Button>
            </Stack>
          </Stack>
        )}

        {/* ── wniosek o zmianę pojazdu po wyjeździe (21.10.2026) ──────── */}
        {showVehicleChangeRequest && (
          <Stack spacing={0.5} data-testid="vehicle-change-request">
            <Typography variant="body2">{departedVehicleChangeText(departure)}</Typography>
            <Stack direction="row" spacing={1} useFlexGap sx={ROW_SX}>
              <Box sx={{ ...FIELD_SX, minWidth: 280 }}>
                <VehiclePicker
                  value={proposedCarId}
                  onChange={setProposedCarId}
                  disabled={busy}
                  label="Proponowany pojazd"
                  helperText="Inny pojazd z Twojej puli"
                  forOrder={{
                    requiredVehicleType:
                      typeof record.assignedVehicleType === 'string'
                        ? record.assignedVehicleType
                        : null,
                  }}
                />
              </Box>
              <TextField
                size="small"
                label="Powód zmiany (wymagany)"
                required
                value={vehicleChangeReason}
                onChange={(e) => setVehicleChangeReason(e.target.value)}
                disabled={busy}
                sx={FIELD_SX}
                helperText={`Co najmniej ${MIN_UNASSIGN_REASON_LENGTH} znaków — przeczyta go Team Leader`}
              />
              <Button
                sx={BUTTON_SX}
                variant="outlined"
                disabled={
                  busy ||
                  proposedCarId === null ||
                  proposedCarId === record.assignedCarId ||
                  vehicleChangeReason.trim().length < MIN_UNASSIGN_REASON_LENGTH
                }
                onClick={() =>
                  run(async () => {
                    await requestVehicleChange(record.id, proposedCarId as number, vehicleChangeReason);
                    setProposedCarId(null);
                    setVehicleChangeReason('');
                  }, 'Wniosek o zmianę pojazdu wysłany do Team Leadera')
                }
              >
                Wnioskuj o zmianę pojazdu
              </Button>
            </Stack>
          </Stack>
        )}

        {/* ── kandydatura i obsadzenie ─────────────────────────────────── */}
        {canPlan && (
        <Stack direction="row" spacing={1} useFlexGap sx={ROW_SX}>
          <Box sx={{ ...FIELD_SX, minWidth: 280 }}>
            {/* Etykieta z omówienia 01.10.2026 (wcześniej „Pojazd do rozważenia”). */}
            <VehiclePicker
              value={pickedCarId}
              onChange={setPickedCarId}
              disabled={busy}
              label="Sugerowane pojazdy"
              helperText="Szukaj po numerze rejestracyjnym"
              // Lista przy zleceniu: najpierw auta, które obsłużą wymagany typ,
              // bez osobówek, naczep i wózków (test na dev 06.10.2026).
              forOrder={{
                requiredVehicleType:
                  typeof record.assignedVehicleType === 'string'
                    ? record.assignedVehicleType
                    : null,
              }}
            />
          </Box>
          <Button
            sx={BUTTON_SX}
            variant="outlined"
            disabled={busy || pickedCarId === null}
            onClick={() =>
              run(async () => {
                const added: unknown = await addCandidate(record.id, pickedCarId as number, { origin: 'manual' });
                setPickedCarId(null);
                return added;
              }, (added) => withWindowWarning(withVariantScopeNote('Wariant dodany', added), added))
            }
          >
            Dodaj wariant
          </Button>
        </Stack>
        )}

        {assignedLabel ? (
          <Typography variant="body2" data-testid="assigned-car">
            Obsadzone pojazdem:{' '}
            {/* Odsyłacz do karty auta tylko z planowaniem FTL — Trade floty nie widzi (01.10.2026). */}
            {canPlan && assignedId !== null ? (
              <AssignedCarLink carId={assignedId} label={assignedLabel} />
            ) : (
              <strong>{assignedLabel}</strong>
            )}
          </Typography>
        ) : null}

        {/* Obsadzenie i zmiana pojazdu (`assign-car`) — tylko z `FTL_ASSIGN`;
            planista widzi „Obsadź” nieaktywny z podpowiedzią (20.10.2026,
            `assignButtonForPlanner`). */}
        {!canAssignVehicle ? (
          plannerSeesDisabledAssign && actions.canAssign ? (
            <Stack direction="row" spacing={1} useFlexGap sx={ROW_SX}>
              <TeamLeaderAssignsButton label={changesAssignment ? 'Zmień pojazd' : 'Obsadź'} />
            </Stack>
          ) : null
        ) : actions.canAssign ? (
          <Stack direction="row" spacing={1} useFlexGap sx={ROW_SX}>
            <TextField
              select
              size="small"
              data-testid="order-assign-car"
              label={changesAssignment ? 'Zmień obsadzenie na' : 'Obsadź pojazdem'}
              value={assignCarId}
              onChange={(e) => setAssignCarId(e.target.value)}
              disabled={busy || assignOptions.length === 0}
              sx={FIELD_SX}
              helperText={
                assignOptions.length === 0
                  ? changesAssignment
                    ? 'Aby zmienić auto, dodaj je najpierw jako wariant'
                    : 'Najpierw dodaj wariant'
                  : 'Pozostałe warianty zostaną zwolnione'
              }
            >
              {assignOptions.map((k) => (
                <MenuItem key={k.id as number} value={String(k.carId)}>
                  {assignOptionLabel(k, ranks)}
                </MenuItem>
              ))}
            </TextField>
            {/*
              Powód zmiany pojazdu (porównanie z makietą FTL, 13.10.2026):
              API przyjmowało go zawsze, ale karta go nie wysyłała, więc
              w dzienniku zmiana auta stała bez uzasadnienia. Opcjonalny —
              czy ma być wymagany (i z listy), to pytanie do Marathonu.
            */}
            {changesAssignment && (
              <TextField
                size="small"
                label="Powód zmiany pojazdu (opcjonalny)"
                value={assignReason}
                onChange={(e) => setAssignReason(e.target.value)}
                disabled={busy || assignOptions.length === 0}
                sx={FIELD_SX}
                helperText="Trafi do historii zmian zlecenia"
              />
            )}
            <Button
              sx={BUTTON_SX}
              variant="contained"
              color="success"
              disabled={busy || !assignCarId}
              onClick={() => (lateWarning ? setLateConfirmOpen(true) : void assignSelected())}
              // Kontrakt `data-testid` paczki (S-22.10-02): aktywne „Obsadź” (nie „Zmień pojazd”).
              data-testid={changesAssignment ? undefined : 'ftl-zlecenie-obsadz-aktywny'}
            >
              {changesAssignment ? 'Zmień pojazd' : 'Obsadź'}
            </Button>
            {lateWarning ? (
              <Box sx={{ flexBasis: '100%' }}>
                <LateAssignmentWarning warning={lateWarning} testId="assign-late-warning" />
              </Box>
            ) : null}
            {/* Okno ostrzeżenia — furtka Team Leadera: bez powodu, bez blokady. */}
            <Dialog
              open={lateConfirmOpen && lateWarning !== null}
              onClose={() => setLateConfirmOpen(false)}
              data-testid="assign-late-dialog"
            >
              <DialogTitle>Obsadzić mimo braku czasu?</DialogTitle>
              <DialogContent>
                {lateWarning ? (
                  <LateAssignmentWarning warning={lateWarning} testId="assign-late-dialog-warning" />
                ) : null}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setLateConfirmOpen(false)}>Anuluj</Button>
                <Button
                  variant="contained"
                  color="warning"
                  data-testid="assign-late-confirm"
                  onClick={() => {
                    setLateConfirmOpen(false);
                    void assignSelected();
                  }}
                >
                  Obsadź mimo braku czasu
                </Button>
              </DialogActions>
            </Dialog>
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {/* Powód przychodzi z API — nie zgadujemy go tutaj. */}
            Obsadzenie niedostępne:{' '}
            {actions.assignBlockedReason ?? 'powód nieznany'}
          </Typography>
        )}

        {/* ── ryzyko ───────────────────────────────────────────────────── */}
        <Stack direction="row" spacing={1} useFlexGap sx={ROW_SX}>
          {record.atRisk ? (
            <Button
              sx={BUTTON_SX}
              variant="outlined"
              disabled={busy}
              onClick={() =>
                run(() => setRisk(record.id, false), 'Zgłoszenie ryzyka wycofane')
              }
            >
              Wycofaj zgłoszenie ryzyka
            </Button>
          ) : (
            <>
              <TextField
                size="small"
                label="Powód ryzyka"
                value={riskReason}
                onChange={(e) => setRiskReason(e.target.value)}
                disabled={busy}
                sx={FIELD_SX}
                // Backend odrzuca zgłoszenie bez powodu — mówimy o tym
                // z wyprzedzeniem, zamiast pozwolić kliknąć i dostać błąd.
                helperText="Wymagany przy zgłoszeniu"
              />
              <Button
                sx={BUTTON_SX}
                variant="outlined"
                color="error"
                disabled={busy || !riskReason.trim()}
                onClick={() =>
                  run(
                    () => setRisk(record.id, true, { reason: riskReason }),
                    'Ryzyko zgłoszone',
                  )
                }
              >
                Zgłoś ryzyko
              </Button>
            </>
          )}
        </Stack>

        {/* ── termin ważności — prezlecenie albo termin już ustawiony ──── */}
        {showsValidUntil(record) ? (
          <Stack direction="row" spacing={1} useFlexGap sx={ROW_SX}>
            <TextField
              size="small"
              type="date"
              label="Termin ważności"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              disabled={busy}
              // MUI zdjęło InputLabelProps — etykieta pola daty musi być
              // podniesiona, inaczej nachodzi na wartość.
              slotProps={{ inputLabel: { shrink: true } }}
              sx={FIELD_SX}
            />
            <Button
              sx={BUTTON_SX}
              variant="outlined"
              disabled={busy || !expiryDate}
              onClick={() => runValidUntil(expiryDate)}
            >
              Ustaw
            </Button>
            <Button
              sx={BUTTON_SX}
              variant="text"
              disabled={busy || !record.validUntil}
              onClick={() => runValidUntil(null)}
            >
              Usuń termin
            </Button>
          </Stack>
        ) : null}
      </Stack>
      {override.dialog}
    </SectionCard>
  );
};

/** Stan panelu należy do rekordu — przejście na inny rekord zaczyna od zera (`recordScoped`). */
export const OrderActions = recordScoped(OrderActionsPanel);
